/**
 * IndustryLandingPage - Branchen-spezifische Landing-Pages
 * 
 * Unterstützte Branchen:
 * - /bau - Bauwesen
 * - /labor - Labore/Forschung
 * - /aerospace - Flugzeugbau
 * - /shipbuilding - Schiffbau
 * - /international - Internationale Projekte
 * - /galabau - Garten- und Landschaftsbau
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, HelpCircle, X, Award } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import HelpPage from './help/HelpPage';
import { Button } from './ui/button';

// GaLaBau Benefits Modal Component
const GaLaBauBenefitsModal = ({ isOpen, onClose, t }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Image */}
        <div className="relative h-48 sm:h-64 flex-shrink-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/karx5vhu_Geschwungene%20Naturstein-Villenauffahrt%20mit%20beleuchtetem%20Garten_ideogram.png"
            alt="GaLaBau Projekt"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
              {t('industry.galabau.benefitsTitle', 'BABELMIX für den Garten- und Landschaftsbau')}
            </h2>
            <p className="text-white/90 text-sm sm:text-base mt-1">
              {t('industry.galabau.benefitsSubtitle', 'Entwickelt für verteilte Baustellen. Für internationale Teams. Für die Realität draußen.')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full p-2 transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="prose prose-green max-w-none">
            {/* Intro */}
            <p className="text-gray-700 text-lg leading-relaxed">
              {t('industry.galabau.benefits.intro1', 'Im GaLaBau laufen Projekte selten zentral. Mehrere Baustellen gleichzeitig, wechselnde Kolonnen, Subunternehmer, Saisonkräfte – oft mit unterschiedlichen Muttersprachen.')}
            </p>
            <p className="text-gray-700 font-medium">
              {t('industry.galabau.benefits.intro2', 'Kommunikationsfehler sind hier kein theoretisches Problem, sondern kosten bares Geld.')}
            </p>
            <p className="text-green-700 font-semibold text-lg">
              {t('industry.galabau.benefits.intro3', 'BABELMIX bringt Struktur, Klarheit und Geschwindigkeit in genau dieses Umfeld.')}
            </p>
            
            <hr className="my-6 border-green-200" />
            
            {/* Section 1 */}
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
              {t('industry.galabau.benefits.section1Title', 'Wer profitiert konkret?')}
            </h3>
            
            <div className="space-y-4 ml-2">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-bold text-green-800 mb-2">👷 {t('industry.galabau.benefits.foreman', 'Vorarbeiter')}</h4>
                <p className="text-gray-700 text-sm">
                  {t('industry.galabau.benefits.foremanText', 'Sie betreuen häufig mehrere Baustellen parallel. Statt ständig zwischen Projekten zu pendeln, erhalten sie strukturierte Updates in Echtzeit – inklusive Fotos, Aufgabenstatus und Rückfragen aus dem Team.')}
                  <br /><strong>{t('industry.galabau.benefits.foremanHighlight', 'Fehler werden früher erkannt. Eingriffe erfolgen rechtzeitig.')}</strong>
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-bold text-blue-800 mb-2">📋 {t('industry.galabau.benefits.projectManager', 'Bau- und Projektleiter')}</h4>
                <p className="text-gray-700 text-sm">
                  {t('industry.galabau.benefits.projectManagerText', 'Der Tagesbericht entsteht nicht mehr aus WhatsApp-Nachrichten, Anrufen und Notizzetteln. Alles ist automatisch nach Projekt und Aufgabe strukturiert dokumentiert.')}
                  <br /><strong>{t('industry.galabau.benefits.projectManagerHighlight', 'Zeitersparnis: oft 1–2 Stunden pro Tag.')}</strong>
                </p>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-4">
                <h4 className="font-bold text-amber-800 mb-2">🌍 {t('industry.galabau.benefits.workers', 'Kolonnen und Facharbeiter')}</h4>
                <p className="text-gray-700 text-sm">
                  {t('industry.galabau.benefits.workersText', 'Im GaLaBau arbeiten viele internationale Mitarbeiter. Mit BABELMIX können sie Fragen und Fortschritte in ihrer eigenen Sprache erfassen. Die Verantwortlichen erhalten alles sauber übersetzt und eingeordnet.')}
                  <br /><strong>{t('industry.galabau.benefits.workersHighlight', 'Missverständnisse werden reduziert – und niemand muss „raten", was gemeint war.')}</strong>
                </p>
              </div>
            </div>
            
            <p className="text-gray-600 italic text-sm mt-4">
              {t('industry.galabau.benefits.section1Note', 'Alle Aufgaben, Pläne, Fotos und Fortschritte sind projektbezogen organisiert – z.B. nach Pflasterarbeiten, Entwässerung, Pflanzung oder Bewässerung.')}
            </p>
            
            <hr className="my-6 border-green-200" />
            
            {/* Section 2 */}
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
              {t('industry.galabau.benefits.section2Title', 'Welche typischen Probleme werden gelöst?')}
            </h3>
            
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                {t('industry.galabau.benefits.problem1', 'Verstreute Kommunikation über mehrere Baustellen')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                {t('industry.galabau.benefits.problem2', 'Sprachbarrieren zwischen Bauleitung und Team')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                {t('industry.galabau.benefits.problem3', 'Unklare Aufgabenverteilung')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                {t('industry.galabau.benefits.problem4', 'Arbeiten nach veralteten Plänen')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                {t('industry.galabau.benefits.problem5', 'Hoher Dokumentationsaufwand für Auftraggeber und Kommunen')}
              </li>
            </ul>
            
            <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
              <p className="text-gray-700 text-sm">
                <strong>{t('industry.galabau.benefits.marginWarning', 'Die Margen im GaLaBau sind häufig knapp kalkuliert.')}</strong><br />
                {t('industry.galabau.benefits.marginWarningDetail', 'Ein falsch gesetztes Gefälle, eine missverstandene Entwässerung oder eine fehlerhafte Pflanzenauswahl kann schnell zu vier- oder fünfstelligen Mehrkosten führen.')}
              </p>
            </div>
            
            <p className="text-gray-700">
              {t('industry.galabau.benefits.section2Note1', 'Viele Unternehmen kompensieren das durch permanente Präsenz auf der Baustelle. Das funktioniert – kostet aber enorm Zeit und Ressourcen.')}
            </p>
            <p className="text-green-700 font-medium">
              {t('industry.galabau.benefits.section2Note2', 'BABELMIX verbessert den Informationsfluss und reduziert den Bedarf an ständiger physischer Kontrolle.')}
            </p>
            
            <hr className="my-6 border-green-200" />
            
            {/* Section 3 */}
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
              {t('industry.galabau.benefits.section3Title', 'Wie startet die Nutzung?')}
            </h3>
            
            <p className="text-gray-700">
              <strong className="text-green-700">{t('industry.galabau.benefits.section3Text1', 'Die Grundversion ist kostenlos.')}</strong> {t('industry.galabau.benefits.section3Text2', 'Ein Vorarbeiter oder Projektleiter kann direkt beginnen – ohne langwierige Freigaben der Geschäftsleitung.')}
            </p>
            <p className="text-gray-700">
              {t('industry.galabau.benefits.section3Text3', 'Da regelmäßig Subunternehmer und externe Teams eingebunden sind, verbreitet sich die Nutzung organisch innerhalb der Projekte.')}
            </p>
            <p className="text-gray-600 italic text-sm">
              {t('industry.galabau.benefits.section3Note', 'In manchen Fällen starten sogar die Kolonnen selbst, um über mehrere Baustellen hinweg den Überblick zu behalten.')}
            </p>
            
            <hr className="my-6 border-green-200" />
            
            {/* Section 4 */}
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
              {t('industry.galabau.benefits.section4Title', 'Wirtschaftlicher Effekt im GaLaBau')}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-4">
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">5.000–20.000 €</div>
                <div className="text-sm text-gray-600">{t('industry.galabau.benefits.costPerError', 'Kosten pro Koordinationsfehler')}</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">1–2 h</div>
                <div className="text-sm text-gray-600">{t('industry.galabau.benefits.dailyDocumentation', 'täglich für Dokumentation')}</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">2–3 h</div>
                <div className="text-sm text-gray-600">{t('industry.galabau.benefits.weeklySavings', 'Ersparnis pro Woche (6-Mann-Team)')}</div>
              </div>
            </div>
            
            <p className="text-gray-700">
              {t('industry.galabau.benefits.section4Note', 'Bei mehreren parallel laufenden Baustellen summiert sich das erheblich.')}
            </p>
            
            <hr className="my-6 border-green-200" />
            
            {/* Section 5 */}
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
              {t('industry.galabau.benefits.section5Title', 'Ideale Zielgruppe')}
            </h3>
            
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600">🌿</span>
                {t('industry.galabau.benefits.target1', 'GaLaBau-Betriebe mit internationalen Teams')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">🏗️</span>
                {t('industry.galabau.benefits.target2', 'Unternehmen mit mehreren gleichzeitigen Baustellen')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">🏛️</span>
                {t('industry.galabau.benefits.target3', 'Firmen mit kommunalen oder gewerblichen Auftraggebern, die strukturierte Dokumentation verlangen')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">📱</span>
                {t('industry.galabau.benefits.target4', 'Betriebe, die über „Telefon & WhatsApp" hinausgewachsen sind')}
              </li>
            </ul>
            
            <hr className="my-6 border-green-200" />
            
            {/* Section 6 */}
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
              {t('industry.galabau.benefits.section6Title', 'Warum ist das gerade im GaLaBau entscheidend?')}
            </h3>
            
            <p className="text-gray-700">
              <strong>{t('industry.galabau.benefits.section6Text1', 'GaLaBau-Projekte sind dynamisch.')}</strong><br />
              {t('industry.galabau.benefits.section6Text2', 'Wetter verschiebt Abläufe. Kolonnen wechseln. Material kommt verspätet. Anpassungen passieren laufend.')}
            </p>
            <p className="text-gray-700 font-medium">
              {t('industry.galabau.benefits.section6Text3', 'Wenn Kommunikation nicht strukturiert ist, entstehen Fehler – und Fehler kosten.')}
            </p>
            
            <div className="bg-green-100 rounded-xl p-5 my-4">
              <h4 className="font-bold text-green-800 mb-3">{t('industry.galabau.benefits.providesTitle', 'BABELMIX sorgt für:')}</h4>
              <ul className="space-y-2 text-green-800">
                <li>✓ {t('industry.galabau.benefits.provides1', 'Klare Verantwortlichkeiten')}</li>
                <li>✓ {t('industry.galabau.benefits.provides2', 'Mehrsprachige Kommunikation in Echtzeit')}</li>
                <li>✓ {t('industry.galabau.benefits.provides3', 'Fotobasierte Dokumentation')}</li>
                <li>✓ {t('industry.galabau.benefits.provides4', 'Strukturierte Tagesberichte')}</li>
                <li>✓ {t('industry.galabau.benefits.provides5', 'Übersicht über alle laufenden Projekte')}</li>
              </ul>
            </div>
            
            <p className="text-gray-600 italic border-l-4 border-green-400 pl-4 my-4">
              {t('industry.galabau.benefits.conclusion1', 'Es ersetzt keine handwerkliche Kompetenz.')}<br />
              <strong>{t('industry.galabau.benefits.conclusion2', 'Es sorgt dafür, dass sie nicht durch schlechte Kommunikation ausgebremst wird.')}</strong>
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-4 bg-gray-50 border-t">
          <Button
            onClick={onClose}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            {t('industry.galabau.benefitsUnderstood', 'Verstanden')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Branchen-Konfiguration mit Bildern und Texten
const INDUSTRY_CONFIG = {
  galabau: {
    titleKey: 'industry.galabau.title',
    titleFallback: 'Babelmix für Garten- und Landschaftsbau',
    subtitleKey: 'industry.galabau.subtitle',
    subtitleFallback: 'Perfekte Kommunikation bei jedem Außenprojekt',
    slides: [
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/bwf4h6f9_1_Mehrere%20Gewerke%20parallel%3F.png',
        textKey: 'industry.galabau.slide1',
        textFallback: 'Mehrere Gewerke parallel?'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/4j0voyvn_2_Der%20Projektleiter%20muss%20das%20Chaos%20im%20Griff%20haben%3F.png',
        textKey: 'industry.galabau.slide2',
        textFallback: 'Der Projektleiter muss das Chaos im Griff haben?'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/rtlunj25_3_Internationale%20Subunternehmer%3F.png',
        textKey: 'industry.galabau.slide3',
        textFallback: 'Internationale Subunternehmer?'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/6beh8qfy_4_Teams%20mit%20verschiedenen%20Sprachen%3F.png',
        textKey: 'industry.galabau.slide4',
        textFallback: 'Teams mit verschiedenen Sprachen?'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/9rv4e86k_5_Automatische%20Verteilung%20der%20Aufgaben%3F.png',
        textKey: 'industry.galabau.slide5',
        textFallback: 'Automatische Verteilung der Aufgaben?'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/n63mzbj0_6_Live-Baustellen-Nachfragen%3F.png',
        textKey: 'industry.galabau.slide6',
        textFallback: 'Live-Baustellen-Nachfragen?'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/itadai8d_7_Qualita%CC%88tskontrolle%20und%20Dokumentation.png',
        textKey: 'industry.galabau.slide7',
        textFallback: 'Qualitätskontrolle und Dokumentation'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/zbvsdku7_8_Projekte%20in%20Zeit%20%26%20Budget%20fertig%21.png',
        textKey: 'industry.galabau.slide8',
        textFallback: 'Projekte in Zeit & Budget fertig!'
      },
      {
        image: 'https://customer-assets.emergentagent.com/job_intl-upgrade/artifacts/bf68daen_9_%20Glu%CC%88ckliche%20Kunden%20%3D%20ho%CC%88herer%20Umsatz.png',
        textKey: 'industry.galabau.slide9',
        textFallback: 'Glückliche Kunden = höherer Umsatz'
      }
    ],
    color: 'green'
  },
  bau: {
    titleKey: 'industry.bau.title',
    titleFallback: 'Babelmix für Bauprojekte',
    subtitleKey: 'industry.bau.subtitle',
    subtitleFallback: 'Klare Kommunikation auf jeder Baustelle',
    slides: [
      {
        image: 'https://images.unsplash.com/photo-1692166567037-4009225486ac?w=800&q=80',
        textKey: 'industry.bau.slide1',
        textFallback: 'Zehn WhatsApp-Gruppen für ein Bauprojekt?'
      },
      {
        image: 'https://images.unsplash.com/photo-1692166525289-be57203f44a9?w=800&q=80',
        textKey: 'industry.bau.slide2',
        textFallback: 'Sprachbarrieren auf der Baustelle?'
      },
      {
        image: 'https://images.pexels.com/photos/35300832/pexels-photo-35300832.jpeg?w=800',
        textKey: 'industry.bau.slide3',
        textFallback: 'Mit Babelmix: Alle verstehen sich!'
      }
    ],
    color: 'amber'
  },
  labor: {
    titleKey: 'industry.labor.title',
    titleFallback: 'Babelmix für Labore & Forschung',
    subtitleKey: 'industry.labor.subtitle',
    subtitleFallback: 'Effiziente Kommunikation im Labor',
    slides: [
      {
        image: 'https://images.unsplash.com/photo-1707944746058-4da338d0f827?w=800&q=80',
        textKey: 'industry.labor.slide1',
        textFallback: 'Internationale Forschungsteams koordinieren'
      },
      {
        image: 'https://images.unsplash.com/photo-1630959302862-82cec6653d60?w=800&q=80',
        textKey: 'industry.labor.slide2',
        textFallback: 'Dokumentation in Echtzeit'
      },
      {
        image: 'https://images.pexels.com/photos/8442375/pexels-photo-8442375.jpeg?w=800',
        textKey: 'industry.labor.slide3',
        textFallback: 'Feld-Modus für Labore mit Handschuhen'
      }
    ],
    color: 'blue'
  },
  aerospace: {
    titleKey: 'industry.aerospace.title',
    titleFallback: 'Babelmix für Luft- und Raumfahrt',
    subtitleKey: 'industry.aerospace.subtitle',
    subtitleFallback: 'Präzise Kommunikation im Flugzeugbau',
    slides: [
      {
        image: 'https://images.pexels.com/photos/586092/pexels-photo-586092.jpeg?w=800',
        textKey: 'industry.aerospace.slide1',
        textFallback: 'Komplexe Lieferketten koordinieren'
      },
      {
        image: 'https://images.pexels.com/photos/586093/pexels-photo-586093.jpeg?w=800',
        textKey: 'industry.aerospace.slide2',
        textFallback: 'Qualitätsdokumentation in Echtzeit'
      },
      {
        image: 'https://images.unsplash.com/photo-1690613139174-5074641eaa0b?w=800&q=80',
        textKey: 'industry.aerospace.slide3',
        textFallback: 'Internationale Teams, eine Plattform'
      }
    ],
    color: 'sky'
  },
  shipbuilding: {
    titleKey: 'industry.shipbuilding.title',
    titleFallback: 'Babelmix für Schiffbau',
    subtitleKey: 'industry.shipbuilding.subtitle',
    subtitleFallback: 'Effiziente Kommunikation auf der Werft',
    slides: [
      {
        image: 'https://images.unsplash.com/photo-1758574697274-ebbbd08ec0d9?w=800&q=80',
        textKey: 'industry.shipbuilding.slide1',
        textFallback: 'Große Projekte, klare Kommunikation'
      },
      {
        image: 'https://images.pexels.com/photos/8193005/pexels-photo-8193005.jpeg?w=800',
        textKey: 'industry.shipbuilding.slide2',
        textFallback: 'Dokumentation direkt auf der Werft'
      },
      {
        image: 'https://images.pexels.com/photos/5364719/pexels-photo-5364719.jpeg?w=800',
        textKey: 'industry.shipbuilding.slide3',
        textFallback: 'Multinationale Teams verbinden'
      }
    ],
    color: 'cyan'
  },
  international: {
    titleKey: 'industry.international.title',
    titleFallback: 'Babelmix für Internationale Projekte',
    subtitleKey: 'industry.international.subtitle',
    subtitleFallback: 'Grenzenlose Zusammenarbeit',
    slides: [
      {
        image: 'https://images.unsplash.com/photo-1764726354430-1b85fa37234f?w=800&q=80',
        textKey: 'industry.international.slide1',
        textFallback: 'Teams über Kontinente hinweg'
      },
      {
        image: 'https://images.pexels.com/photos/3183174/pexels-photo-3183174.jpeg?w=800',
        textKey: 'industry.international.slide2',
        textFallback: 'Jeder spricht seine Sprache'
      },
      {
        image: 'https://images.pexels.com/photos/7993894/pexels-photo-7993894.jpeg?w=800',
        textKey: 'industry.international.slide3',
        textFallback: 'Automatische Übersetzung in 19 Sprachen'
      }
    ],
    color: 'emerald'
  }
};

const IndustryLandingPage = ({ industry, onClickToRegister, onClickToLogin }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showGaLaBauBenefits, setShowGaLaBauBenefits] = useState(false);

  const config = INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.bau;
  const slides = config.slides;

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);
    
    return () => clearInterval(timer);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = useCallback((index) => {
    setIsAutoPlaying(false);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, 150);
  }, []);

  const goNext = useCallback(() => {
    setIsAutoPlaying(false);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setIsTransitioning(false);
    }, 150);
  }, [slides.length]);

  const goPrev = useCallback(() => {
    setIsAutoPlaying(false);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setIsTransitioning(false);
    }, 150);
  }, [slides.length]);

  const colorClasses = {
    green: 'from-green-600 to-emerald-700',
    amber: 'from-amber-600 to-orange-700',
    blue: 'from-blue-600 to-indigo-700',
    sky: 'from-sky-600 to-blue-700',
    cyan: 'from-cyan-600 to-teal-700',
    emerald: 'from-emerald-600 to-green-700'
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorClasses[config.color]} flex flex-col`}>
      {/* Header */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 flex-shrink-0">
        <LanguageSelector />
        <button
          onClick={() => setShowHelp(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors text-sm"
          data-testid="help-docs-button"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="hidden sm:inline">{t('common.helpDocs', 'Help & Documentation')}</span>
        </button>
      </header>

      {/* Help Page Modal */}
      {showHelp && (
        <HelpPage 
          onClose={() => setShowHelp(false)} 
          currentUser={null}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-6 overflow-hidden">
        {/* Title */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-2 drop-shadow-lg">
          {t(config.titleKey, config.titleFallback)}
        </h1>
        <p className="text-base sm:text-lg text-white/90 text-center mb-6 max-w-xl">
          {t(config.subtitleKey, config.subtitleFallback)}
        </p>

        {/* Slideshow Container */}
        <div className="relative w-full max-w-2xl aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl bg-black/20">
          {/* Image */}
          <div className={`absolute inset-0 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <img
              src={slides[currentSlide].image}
              alt={t(slides[currentSlide].textKey, slides[currentSlide].textFallback)}
              className="w-full h-full object-cover"
            />
            {/* Overlay with text */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-xl sm:text-2xl font-semibold text-white text-center drop-shadow-lg">
                {t(slides[currentSlide].textKey, slides[currentSlide].textFallback)}
              </p>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/30 backdrop-blur-sm text-white hover:bg-white/50 transition-colors"
            aria-label={t('landing.prevImage', 'Previous')}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/30 backdrop-blur-sm text-white hover:bg-white/50 transition-colors"
            aria-label={t('landing.nextImage', 'Next')}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Button
            onClick={onClickToRegister}
            size="lg"
            className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 py-3 text-lg rounded-full shadow-lg"
            data-testid="register-button"
          >
            {t('landing.registerFree', 'Register for free')}
          </Button>
          <Button
            onClick={onClickToLogin}
            variant="outline"
            size="lg"
            className="border-2 border-white text-white hover:bg-white/20 font-semibold px-8 py-3 text-lg rounded-full"
            data-testid="login-button"
          >
            {t('auth.login', 'Log in')}
          </Button>
        </div>

        {/* GaLaBau Benefits Button - only shown on galabau page */}
        {industry === 'galabau' && (
          <div className="mt-6">
            <Button
              onClick={() => setShowGaLaBauBenefits(true)}
              variant="outline"
              size="lg"
              className="border-2 border-white/70 text-white hover:bg-white/20 font-medium px-6 py-2 rounded-full flex items-center gap-2"
              data-testid="galabau-benefits-button"
            >
              <Award className="w-5 h-5" />
              {t('industry.galabau.benefitsButton', 'Die 6 wichtigsten Vorteile von Babelmix für den GaLaBau')}
            </Button>
          </div>
        )}

        {/* Link to general landing page */}
        <p className="mt-6 text-white/80 text-sm">
          {t('industry.seeAll', 'See all industries')} → <a href="/" className="underline hover:text-white">babelmix.com</a>
        </p>
      </main>

      {/* GaLaBau Benefits Modal */}
      <GaLaBauBenefitsModal 
        isOpen={showGaLaBauBenefits} 
        onClose={() => setShowGaLaBauBenefits(false)}
        t={t}
      />

      {/* Footer */}
      <footer className="px-4 py-4 text-center text-white/70 text-sm">
        © 2026 Babelmix - {t('landing.subtitle', 'Project communication reimagined')}
      </footer>
    </div>
  );
};

export default IndustryLandingPage;
